import React, {
  memo, useState, useMemo, useCallback,
  PropsWithChildren, Dispatch, SetStateAction
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Accordion, AccordionItem as Item } from '@szhsin/react-accordion';

import { Icon } from '@common/components/icon';
import { Button } from '@common/components/button';
import ModalWindow from '@common/components/modal/modal-window';
import ModalContent from '@common/components/modal/modal-content';
import { Overview } from '@common/components/overview';
import { Container } from '@common/components/container';
import { AlertService } from '@common/services/alert';
import {
  PulseSurveyQuestion, PulseSurveyQuestionTranslation,
  PulseSurveyQuestionSelection, PulseSurveyResponse
} from '@modules/survey/types/objects';
import { EPulseSurveyCategories } from '@modules/survey/definitions';
import { Checkbox } from '@common/components/form/inputs/checkbox';
import CustomQuestionModal from './custom-question-modal';
import Confirm from '@common/components/confirm-button';
import { createCustomQuestion, deleteCustomQuestion, updateQuestion } from '@modules/survey/actions';

const AccordionItem = ({
  header, total, count, icon, ...rest
}: any) => {
  const { t } = useTranslation();
  return (
    <Item
      {...rest}
      header={
        <>
          <Icon type="expand_more" className="chevron-down" />
          <Icon type={icon} className="questionIcon" size={16} />
          <h3>{header}</h3>
          <h6 className="questionsCount">
            {t('survey:pulse_survey_questions_count', { count, total })}
          </h6>
        </>
      }
    />
  );
};


type QuestionSettingsModalProps = PropsWithChildren<{
  translations: PulseSurveyQuestionTranslation[];
  isCustom?: boolean;
  onEditQuestion?: () => void;
  onDeleteQuestion?: () => void;
}>;

const QuestionSettingsModal = memo(({
  children,
  translations,
  isCustom = false,
  onEditQuestion,
  onDeleteQuestion
}: QuestionSettingsModalProps) => {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const onHide = useCallback(() => setShow(false), [setShow]);
  const onShow = useCallback(() => setShow(true), []);

  const trans = useSelector((state: Record<string, any>) => state.language.translations);
  // const [boost, setBoost] = useState<boolean>(false);

  return (
    <ModalWindow
      trigger={children}
      show={show}
      onHide={onHide}
      onShow={onShow}
      className="PulseSurveyQuestionTranslationsModal"
    >
      <ModalContent
        title={t('survey:pulse_survey_question_settings')}
        onHide={onHide}
        hideConfirm
        footerChildren={
          isCustom && (onEditQuestion || onDeleteQuestion) ? (
            <>
              {onDeleteQuestion && (
                <Button
                  type="primary"
                  onClick={() => {
                    onDeleteQuestion();
                    onHide();
                  }}
                >
                  {t('survey:pulse_survey_custom_question_delete_confirm_delete')}
                </Button>
              )}
              {onEditQuestion && (
                <Button
                  type="primary"
                  onClick={() => {
                    onEditQuestion();
                    onHide();
                  }}
                >
                  {t('survey:pulse_survey_custom_question_edit_translation')}
                </Button>
              )}
            </>
          ) : undefined
        }
      >
        <Overview.Content>
          {/* TODO: link ticket to enable feature */}
          {/* <h3>{ t("survey:pulse_survey_boost") }</h3>
          <p>{ t("survey:pulse_survey_boost_description") }</p>
          <ToggleButton value={boost} label="" onChange={() => setBoost(!boost)} /> */}
          <h3 className="translationsTitle">
            {t('survey:pulse_survey_translations_modal_title')}
          </h3>
          <p>{t('survey:pulse_survey_translations_modal_description')}</p>
          {
            translations.map((tr: PulseSurveyQuestionTranslation) => {
              const country = trans.find(({ locale }: any) => {
                return locale === tr.language_code;
              });
              return (
                <div key={tr.language_code} className="translation">
                  <div>
                    {country && <img src={country.icon} alt={country.name} />}
                    {tr.text}
                  </div>
                </div>
              );
            })
          }
        </Overview.Content>
      </ModalContent>
    </ModalWindow>
  );
});

type AccordionCategoryItemProps = {
  header: string;
  questions: PulseSurveyQuestion[];
  survey: PulseSurveyResponse;
  icon: string;
  category: string;
  setSurvey: Dispatch<SetStateAction<PulseSurveyResponse | null>>;
  initialEntered?: boolean;
  onAddCustomQuestion: (category: string) => void;
  onDeleteQuestion: (question: PulseSurveyQuestion) => void;
  onEditQuestion: (question: PulseSurveyQuestion) => void;
};

const AccordionCategoryItem = memo(({
  header,
  icon,
  questions,
  survey,
  category,
  setSurvey,
  initialEntered = false,
  onAddCustomQuestion,
  onDeleteQuestion,
  onEditQuestion
}: AccordionCategoryItemProps) => {
  const { t } = useTranslation();
  const count = questions.filter((question: PulseSurveyQuestion) => {
    return !!survey.data.questions?.find((q: PulseSurveyQuestionSelection) => {
      return q.question_pool_id === question.id;
    });
  }).length;

  return (
    <AccordionItem
      initialEntered={initialEntered}
      header={header}
      total={questions.length}
      count={count}
      icon={icon}
    >
      {
        questions.map(({ id, text, translations, is_custom }: PulseSurveyQuestion) => {
          const checked = !!survey.data.questions.find((q: PulseSurveyQuestionSelection) => {
            return q.question_pool_id === id;
          });
          return (
            <div key={id} className="accordionQuestion">
              <Checkbox
                size="large"
                label={text}
                value={checked}
                onChange={(value) => {
                  setSurvey((surv: any) => {
                    const existingQuestions = surv?.data?.questions || [];
                    const updatedQuestionsList = value ?
                      [
                        ...existingQuestions,
                        {
                          question_pool_id: id,
                          selected_languages: translations.map(
                            (tr: PulseSurveyQuestionTranslation) => {
                              return tr.language_code;
                            }
                          )
                        }
                      ] :
                      existingQuestions.filter((q: PulseSurveyQuestionSelection) => {
                        return q.question_pool_id !== id;
                      });
                    return {
                      ...surv,
                      data: {
                        ...surv?.data,
                        questions: updatedQuestionsList
                      }
                    };
                  });
                }}
              />
              <div className="questionActions">
                <QuestionSettingsModal
                  translations={translations}
                  isCustom={is_custom}
                  onEditQuestion={is_custom ? () => onEditQuestion({
                    id, text, translations, is_custom, category, organisation_id: null, created_at: ''
                  }) : undefined}
                  onDeleteQuestion={is_custom ? () => onDeleteQuestion({
                    id, text, translations, is_custom, category, organisation_id: null, created_at: ''
                  }) : undefined}
                >
                  <Icon className="settings" type="settings" />
                </QuestionSettingsModal>
              </div>
            </div>
          );
        })
      }
      <div className="addCustomQuestionContainer">
        <Button
          size="small"
          type="primary"
          icon="add"
          onClick={() => onAddCustomQuestion(category)}
          className="addCustomQuestionBtn"
        >
          {t('survey:pulse_survey_add_custom_question')}
        </Button>
      </div>
    </AccordionItem>
  );
});


type QuestionsAccordionProps = {
  questions: PulseSurveyQuestion[];
  survey: PulseSurveyResponse;
  setSurvey: Dispatch<SetStateAction<PulseSurveyResponse | null>>;
  orgId: string;
  surveyId: string;
  setCustomQuestions: (updater: any) => void;
  updateLocalQuestions: (updater: any) => void;
};

const QuestionsAccordion = memo(({
  questions,
  survey,
  setSurvey,
  orgId,
  surveyId,
  setCustomQuestions,
  updateLocalQuestions
}: QuestionsAccordionProps) => {

  const { t } = useTranslation();
  const [showCustomQuestionModal, setShowCustomQuestionModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [savingCustomQuestion, setSavingCustomQuestion] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState<PulseSurveyQuestion | null>(null);
  const [deletingQuestion, setDeletingQuestion] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [questionToEdit, setQuestionToEdit] = useState<PulseSurveyQuestion | null>(null);
  const dispatch = useDispatch();


  const handleAddCustomQuestion = useCallback((category: string) => {
    setSelectedCategory(category);
    setShowCustomQuestionModal(true);
  }, []);

  const handleCloseCustomQuestionModal = useCallback(() => {
    setShowCustomQuestionModal(false);
    setSelectedCategory('');
    setEditMode(false);
    setQuestionToEdit(null);
  }, []);

  const handleEditQuestion = useCallback((question: PulseSurveyQuestion) => {
    setQuestionToEdit(question);
    setSelectedCategory(question.category);
    setEditMode(true);
    setShowCustomQuestionModal(true);
  }, []);

  const handleDeleteQuestion = useCallback((question: PulseSurveyQuestion) => {
    setQuestionToDelete(question);
  }, []);

  const handleCloseDeleteModal = useCallback(() => {
    setQuestionToDelete(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!questionToDelete) return;

    setDeletingQuestion(true);
    try {

      await dispatch(deleteCustomQuestion(surveyId, questionToDelete.id));
      AlertService.success(t('survey:pulse_survey_custom_question_deleted'));
      handleCloseDeleteModal();

      setCustomQuestions((currentQuestions: any) => {
        if (!currentQuestions || !currentQuestions.data) return currentQuestions;

        return {
          ...currentQuestions,
          data: currentQuestions.data.filter((q: PulseSurveyQuestion) => q.id !== questionToDelete.id)
        };
      });

      updateLocalQuestions((currentQuestions: any) => {
        if (!currentQuestions || !currentQuestions.data) return currentQuestions;

        return {
          ...currentQuestions,
          data: currentQuestions.data.filter((q: PulseSurveyQuestion) => q.id !== questionToDelete.id)
        };
      });

    } catch (error: any) {
      AlertService.error(t('survey:pulse_survey_custom_question_delete_error'));
      console.error('Error deleting question:', error);
    } finally {
      setDeletingQuestion(false);
    }
  }, [questionToDelete, orgId, t, handleCloseDeleteModal, setCustomQuestions, updateLocalQuestions]);

  const handleSaveCustomQuestion = useCallback(async (questionData: {
    text: string;
    category: string;
    translations: PulseSurveyQuestionTranslation[];
  }) => {
    setSavingCustomQuestion(true);
    try {
      if (editMode && questionToEdit) {

        const payload = {
          translations: questionData.translations.map((translation) => ({
            languageCode: translation.language_code,
            text: translation.text
          }))
        };

        await dispatch(updateQuestion(questionToEdit.id, payload));

        updateLocalQuestions((currentQuestions: any) => {
          if (!currentQuestions || !currentQuestions.data) return currentQuestions;

          return {
            ...currentQuestions,
            data: currentQuestions.data.map((q: PulseSurveyQuestion) => (q.id === questionToEdit.id
              ? {
                ...q,
                text: questionData.text,
                translations: questionData.translations
              }
              : q)
            )
          };
        });

        AlertService.success(t('survey:pulse_survey_custom_question_updated'));
      } else {

        const payload = {
          questions: [
            {
              selectedLanguages: questionData.translations.map((translation) => translation.language_code),
              translations: questionData.translations.map((translation) => ({
                languageCode: translation.language_code,
                text: translation.text
              }))
            }
          ],
          category: questionData.category,
          pulseSurveyId: surveyId
        };

        const response = await dispatch(createCustomQuestion(payload));
        AlertService.success(t('survey:pulse_survey_custom_question_created'));

        if (response && (response as any).item && (response as any).item[0]) {
          const newQuestion = (response as any).item[0];

          const questionToAdd: PulseSurveyQuestion = {
            id: newQuestion.questionPoolId,
            category: questionData.category,
            organisation_id: null,
            is_custom: true,
            created_at: newQuestion.createdAt,
            text: questionData.text,
            translations: questionData.translations
          };

          updateLocalQuestions((currentQuestions: any) => {
            if (!currentQuestions || !currentQuestions.data) return currentQuestions;

            return {
              ...currentQuestions,
              data: [...currentQuestions.data, questionToAdd]
            };
          });
        }
      }

      handleCloseCustomQuestionModal();

    } catch (error: any) {
      AlertService.error(
        editMode
          ? t('survey:pulse_survey_custom_question_update_error')
          : t('survey:pulse_survey_custom_question_error')
      );
      console.error('Error saving custom question:', error);
    } finally {
      setSavingCustomQuestion(false);
    }
  }, [orgId, surveyId, t, handleCloseCustomQuestionModal, editMode, questionToEdit, setCustomQuestions, updateLocalQuestions]);

  const accordionItems = useMemo(() => {
    const getQuestions = (category: string) => {
      return questions.filter((q: any) => q.category === category);
    };
    return [{
      header: t('survey:pulse_survey_category_personal_growth'),
      icon: 'plant',
      category: EPulseSurveyCategories.PERSONAL_GROWTH,
      questions: getQuestions(EPulseSurveyCategories.PERSONAL_GROWTH)
    }, {
      header: t('survey:pulse_survey_category_recognition_and_feedback'),
      icon: 'speech_cloud',
      category: EPulseSurveyCategories.RECOGNITION_AND_FEEDBACK,
      questions: getQuestions(EPulseSurveyCategories.RECOGNITION_AND_FEEDBACK)
    }, {
      header: t('survey:pulse_survey_category_teamwork'),
      icon: 'team',
      category: EPulseSurveyCategories.TEAMWORK,
      questions: getQuestions(EPulseSurveyCategories.TEAMWORK)
    }, {
      header: t('survey:pulse_survey_category_alignment'),
      icon: 'to_the_center',
      category: EPulseSurveyCategories.ALIGNMENT,
      questions: getQuestions(EPulseSurveyCategories.ALIGNMENT)
    }, {
      header: t('survey:pulse_survey_category_wellness'),
      icon: 'heart_pulse',
      category: EPulseSurveyCategories.WELLNESS,
      questions: getQuestions(EPulseSurveyCategories.WELLNESS)
    }, {
      header: t('survey:pulse_survey_category_relationship_with_manager'),
      icon: 'hierarchy',
      category: EPulseSurveyCategories.RELATIONSHIP_WITH_MANAGER,
      questions: getQuestions(EPulseSurveyCategories.RELATIONSHIP_WITH_MANAGER)
    }, {
      header: t('survey:pulse_survey_category_happiness'),
      icon: 'smile2',
      category: EPulseSurveyCategories.HAPPINESS,
      questions: getQuestions(EPulseSurveyCategories.HAPPINESS)
    }, {
      header: t('survey:pulse_survey_category_inclusion'),
      icon: 'heart_hands',
      category: EPulseSurveyCategories.INCLUSION,
      questions: getQuestions(EPulseSurveyCategories.INCLUSION)
    }, {
      header: t('survey:pulse_survey_category_ambassadorship'),
      icon: 'cockade',
      category: EPulseSurveyCategories.AMBASSADORSHIP,
      questions: getQuestions(EPulseSurveyCategories.AMBASSADORSHIP)
    }, {
      header: t('survey:pulse_survey_category_satisfaction'),
      icon: 'sparks',
      category: EPulseSurveyCategories.SATISFACTION,
      questions: getQuestions(EPulseSurveyCategories.SATISFACTION)
    }];
  }, [questions, t]);

  return (
    <>
      <Accordion transition transitionTimeout={250}>
        {
          accordionItems.map((item: any, index: number) => {
            return (
              <AccordionCategoryItem
                icon={item.icon}
                key={item.header}
                header={item.header}
                category={item.category}
                questions={item.questions}
                survey={survey}
                setSurvey={setSurvey}
                initialEntered={index === 0}
                onAddCustomQuestion={handleAddCustomQuestion}
                onDeleteQuestion={handleDeleteQuestion}
                onEditQuestion={handleEditQuestion}
              />
            );
          })
        }
      </Accordion>

      <CustomQuestionModal
        show={showCustomQuestionModal}
        onHide={handleCloseCustomQuestionModal}
        category={selectedCategory}
        onSave={handleSaveCustomQuestion}
        loading={savingCustomQuestion}
        editMode={editMode}
        questionToEdit={questionToEdit ? {
          id: questionToEdit.id,
          text: questionToEdit.text,
          translations: questionToEdit.translations
        } : undefined}
      />

      {questionToDelete && (
        <Confirm
          title={t('survey:pulse_survey_custom_question_delete_confirm_title')}
          description={t('survey:pulse_survey_custom_question_delete_confirm_message')}
          onConfirm={() => handleConfirmDelete()}
          onCancel={handleCloseDeleteModal}
          isLoading={deletingQuestion}
        />
      )}
    </>
  );
});


type ColumnProps = PropsWithChildren<{
  className?: string;
  id?: string;
}>;

export const Column = memo(({ children, className, id }: ColumnProps) => {
  return (
    <Overview id={id}>
      <Overview.Content className={className}>
        <Container.Column>
          {children}
        </Container.Column>
      </Overview.Content>
    </Overview>
  );
});


type QuestionsEditorProps = {
  questions: PulseSurveyQuestion[];
  survey: PulseSurveyResponse;
  setSurvey: Dispatch<SetStateAction<PulseSurveyResponse | null>>;
  loading?: boolean;
  orgId: string;
  surveyId: string;
  setCustomQuestions: (updater: any) => void;
  updateLocalQuestions: (updater: any) => void;
};

const QuestionsEditor = memo(({
  questions,
  survey,
  setSurvey,
  loading = false,
  orgId,
  surveyId,
  setCustomQuestions,
  updateLocalQuestions
}: QuestionsEditorProps) => {
  const { t } = useTranslation();

  return (
    <Column className={`QuestionsEditor${loading ? ' loading' : ''}`}>
      <div className="titleBar">
        <h2>{t('survey:pulse_survey_questions_title')}</h2>
      </div>
      <p>
        <Trans
          i18nKey="survey:pulse_survey_questions_description"
          components={[
            <a
              rel="noreferrer"
              href="https://google.com"
              target="_blank"
            />
          ]}
        />
      </p>
      <QuestionsAccordion
        questions={questions}
        survey={survey}
        setSurvey={setSurvey}
        orgId={orgId}
        surveyId={surveyId}
        setCustomQuestions={setCustomQuestions}
        updateLocalQuestions={updateLocalQuestions}
      />
    </Column>
  );
});

export default QuestionsEditor;
